import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import SearchIcon from "@mui/icons-material/Search";
import WorkIcon from "@mui/icons-material/Work";
import api from '../config/api';
import useStore from "../config/store";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const OrganizationPositionsLayout = () => {
  const [positions, setPositions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filteredPositions, setFilteredPositions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { authToken, organization, setProject, setProjectName, showAlert } =
    useStore.getState();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [positionsResponse, projectsResponse] = await Promise.all([
        api.get("/positions/by-organization", {
          params: { organizationId: organization },
          headers: { Authorization: `Bearer ${authToken}` },
        }),
        api.get(`/v2/projects/${organization}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        }),
      ]);

      if (positionsResponse.data.response?.status_response === "OK") {
        setPositions(positionsResponse.data.response.data || []);
        setFilteredPositions(positionsResponse.data.response.data || []);
      } else {
        setError(t("positions.loadError"));
      }

      if (
        projectsResponse.data.response?.message ===
        "All projects were successfully obtained"
      ) {
        setProjects(projectsResponse.data.response.data || []);
      }
    } catch (err) {
      console.error("Error loading organization positions:", err);
      setError(t("positions.loadError"));
      showAlert(t("positions.loadError"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredPositions(positions);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = positions.filter((position) => {
      const projectName = getProjectName(position.projectId).toLowerCase();
      return (
        position.name?.toLowerCase().includes(term) ||
        projectName.includes(term)
      );
    });
    setFilteredPositions(filtered);
  }, [searchTerm, positions]);

  const getProjectName = (projectId) => {
    const project = projects.find((p) => p.uid === projectId);
    return project?.name || projectId;
  };

  const handlePositionClick = (position) => {
    const project = projects.find((p) => p.uid === position.projectId);
    if (!project) return;

    setProject(project.uid);
    setProjectName(project.name);
    navigate("/positions");
  };

  return (
    <Box sx={{ pt: "64px" }}>
      {/* Header */}
      <Paper
        sx={{
          p: 1.5,
          pr: 3,
          backgroundColor: "#6A5AF9",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: "0px !important",
        }}
      >
        <Typography
          variant="overline"
          sx={{ color: "white", ml: 4, fontSize: "1rem" }}
        >
          {t("drawer.positions")}
        </Typography>
      </Paper>

      <Box sx={{ p: 3 }}>
        {/* Buscador */}
        <TextField
          fullWidth
          size="small"
          placeholder={t("positions.searchPlaceholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Loading */}
        {loading && (
          <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography color="text.secondary">
              {t("projects.projectLoading")}
            </Typography>
          </Box>
        )}

        {/* Error */}
        {!loading && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Tabla de cargos */}
        {!loading && !error && (
          <>
            {filteredPositions.length === 0 ? (
              <Alert severity="info">
                {searchTerm
                  ? `${t("positions.noPositionsFound")} "${searchTerm}"`
                  : t("positions.noPositions")}
              </Alert>
            ) : (
              <TableContainer component={Paper} elevation={1}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {t("drawer.positions")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {t("projects.title")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {t("positions.hierarchy") || "Jerarquía"}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPositions.map((position) => (
                      <TableRow
                        key={position.uid}
                        hover
                        onClick={() => handlePositionClick(position)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>
                          <Box
                            sx={{ display: "flex", alignItems: "center", gap: 1 }}
                          >
                            <WorkIcon
                              sx={{ fontSize: 16, color: "text.secondary" }}
                            />
                            <Typography variant="body2" fontWeight={500}>
                              {position.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getProjectName(position.projectId)}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {position.jerarquia || "—"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Contador de resultados */}
            {filteredPositions.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                {filteredPositions.length}{" "}
                {t("positions.title").toLowerCase()}
                {searchTerm ? ` — "${searchTerm}"` : ""}
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default OrganizationPositionsLayout;